import React from "react";
import AccordionSummary from "@mui/material/AccordionSummary";
import {
  Accordion,
  IconButton,
  Card,
  Typography,
  Tooltip,
  Stack,
} from "@mui/material";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";
import EditIcon from "@mui/icons-material/Edit";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";

const RuleItem = ({
  item,
  disabled,
  onDelete,
  onEdit,
  onDuplicate,
  dragHandleAttributes,
  dragHandleListeners,
}) => {
  const { t } = useTranslation();
  const isValidNow = (validity) => {
    try {
      if (!validity) return false;
      const now = new Date();
      const fromDate = validity.dateFrom ? new Date(validity.dateFrom) : null;
      const toDate = validity.dateTo ? new Date(validity.dateTo) : null;
      if (fromDate && now < fromDate) return false;
      if (toDate && now > toDate) return false;

      const dayMap = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];
      const todayKey = dayMap[now.getDay()];
      if (Array.isArray(validity.daysOfWeek) && validity.daysOfWeek.length) {
        if (!validity.daysOfWeek.includes(todayKey)) return false;
      }

      if (
        validity.timeWindow &&
        (validity.timeWindow.from || validity.timeWindow.to)
      ) {
        const [fh = "00", fm = "00"] = String(
          validity.timeWindow.from || "00:00"
        ).split(":");
        const [th = "23", tm = "59"] = String(
          validity.timeWindow.to || "23:59"
        ).split(":");
        const fromMin = parseInt(fh, 10) * 60 + parseInt(fm, 10);
        const toMin = parseInt(th, 10) * 60 + parseInt(tm, 10);
        const nowMin = now.getHours() * 60 + now.getMinutes();
        if (fromMin <= toMin) {
          if (nowMin < fromMin || nowMin > toMin) return false;
        } else {
          // window wraps past midnight
          if (!(nowMin >= fromMin || nowMin <= toMin)) return false;
        }
      }
      return true;
    } catch (_) {
      return false;
    }
  };
  const valid = isValidNow(item?.validity);
  return (
    <Card sx={{ mb: 1 }}>
      <Accordion expanded={false} square sx={{ borderRadius: 2 }}>
        <AccordionSummary
          aria-controls="rule-item-content"
          id={`rule-${item?._id}`}
          sx={{
            px: 3,
            flexDirection: "row-reverse",
            "& .MuiAccordionSummary-content": {
              alignItems: "center",
              justifyContent: "space-between",
              "&.Mui-expanded": { margin: "12px 0" },
            },
            ".MuiAccordionSummary-expandIconWrapper": {
              color: "text.secondary",
              transform: "none",
              height: 28,
              width: 28,
              alignItems: "center",
              justifyContent: "center",
              mr: 1,
              "&.Mui-expanded": {
                transform: "none",
                color: "primary.main",
                borderColor: "primary.main",
              },
              "& svg": { fontSize: "1.25rem" },
            },
          }}
        >
          <Div sx={{ display: "flex", alignItems: "center", px: 1 }}>
            <IconButton
              size="small"
              aria-label="drag"
              sx={{ cursor: "grab" }}
              {...(dragHandleAttributes || {})}
              {...(dragHandleListeners || {})}
            >
              <DragIndicatorIcon fontSize="small" />
            </IconButton>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 1 auto", md: 1 },
              flexShrink: 0,
              px: 1,
              minWidth: 120,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              {t("common.active", { defaultValue: "Active" })}
            </Typography>
            <Typography variant={"body1"}>
              {item?.isActive
                ? t("common.yes", { defaultValue: "Yes" })
                : t("common.no", { defaultValue: "No" })}
            </Typography>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 1 auto", md: 1 },
              flexShrink: 0,
              px: 1,
              minWidth: 120,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              {t("pages.dsokCartRules.autoApply", {
                defaultValue: "Auto apply",
              })}
            </Typography>
            <Typography variant={"body1"}>
              {item?.autoApply
                ? t("common.yes", { defaultValue: "Yes" })
                : t("common.no", { defaultValue: "No" })}
            </Typography>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 1 auto", md: 1 },
              flexShrink: 0,
              px: 1,
              minWidth: 120,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              {t("pages.dsokCartRules.isCodeRequired", {
                defaultValue: "Code required",
              })}
            </Typography>
            <Typography variant={"body1"}>
              {item?.isCodeRequired
                ? t("common.yes", { defaultValue: "Yes" })
                : t("common.no", { defaultValue: "No" })}
            </Typography>
          </Div>
          <Div
            sx={{
              flex: { xs: "0 1 auto", md: 1 },
              flexShrink: 0,
              px: 1,
              minWidth: 120,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              {t("pages.dsokCartRules.name", { defaultValue: "Name" })}
            </Typography>
            <Typography variant={"body1"}>{item?.name || ""}</Typography>
          </Div>

          <Div
            sx={{
              flex: { xs: "0 1 auto", md: 1 },
              flexShrink: 0,
              px: 1,
              minWidth: 120,
            }}
          >
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              {t("pages.dsokCartRules.validity", { defaultValue: "Validity" })}
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              {valid ? (
                <CheckCircleOutlineIcon sx={{ color: "success.main" }} />
              ) : (
                <HighlightOffIcon sx={{ color: "error.main" }} />
              )}
              <Typography variant={"body2"} color="text.secondary">
                {valid
                  ? t("common.yes", { defaultValue: "Yes" })
                  : t("common.no", { defaultValue: "No" })}
              </Typography>
            </Stack>
          </Div>

          <Div sx={{ flex: { xs: "0 1 auto", md: 2 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              {t("pages.dsokCartRules.discountTypeLabel", {
                defaultValue: "Discount Type",
              })}
            </Typography>
            <Typography variant={"body1"}>
              {t(`pages.dsokCartRules.discountType.${item?.discountType}`, {
                defaultValue: item?.discountType || "-",
              })}
            </Typography>
          </Div>

          <Div sx={{ flex: { xs: "0 1 auto", md: 2 }, flexShrink: 0, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              {t("pages.dsokCartRules.valueAndMinOrderAmount", {
                defaultValue: "Value/Min Order Amount",
              })}
            </Typography>
            <Typography variant={"body1"}>
              {item?.value + "/" + (item.minOrderAmount ?? "-")}
            </Typography>
          </Div>

          <Div sx={{ flex: { xs: "0 2 auto", md: 0 }, px: 1 }}>
            <Typography
              fontSize={"12px"}
              variant={"h6"}
              color={"text.secondary"}
              mb={0.25}
              sx={{ display: { xs: "none", md: "block" } }}
            >
              {t("pages.presentation.actions")}
            </Typography>
            <Div
              sx={{
                display: "flex",
                flexDirection: "row",
                gap: 0.5,
                flexWrap: "nowrap",
                minWidth: 120,
                alignItems: "center",
              }}
            >
              <IconButton
                size="small"
                variant={"contained"}
                disabled={disabled}
                onClick={onEdit}
              >
                <Tooltip title={t("buttons.edit")} placement="top-start">
                  <EditIcon />
                </Tooltip>
              </IconButton>
              <IconButton
                size="small"
                variant={"contained"}
                disabled={disabled}
                onClick={() => onDuplicate && onDuplicate(item)}
              >
                <Tooltip title={t("buttons.copy")} placement="top-start">
                  <ContentCopyIcon />
                </Tooltip>
              </IconButton>
              <IconButton
                size="small"
                variant={"contained"}
                disabled={disabled}
                onClick={() => onDelete(item)}
              >
                <Tooltip title={t("buttons.delete")} placement="top-start">
                  <DeleteForeverIcon />
                </Tooltip>
              </IconButton>
            </Div>
          </Div>
        </AccordionSummary>
      </Accordion>
    </Card>
  );
};

export default RuleItem;
